// eslint-disable-next-line no-restricted-imports
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  isAxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { data } from 'react-router';
import isServer from '~/scripts/isServer';
import { logger } from '~/scripts/logger';

const onRequest = async (
  config: InternalAxiosRequestConfig,
  request?: Request
): Promise<InternalAxiosRequestConfig> => {
  // send credentials for API requests

  if (isApiUrl(config.baseURL)) {
    // send cookies (credentials) with API requests
    config.withCredentials = true;

    if (isServer() && request) {
      const ignoredHeaders = new Set([
        'connection',
        'keep-alive',
        'transfer-encoding',
        'te',
        'trailers',
        'upgrade',
        'proxy-authorization',
        'proxy-authenticate',
        'host',
        'content-length',
      ]);
      request.headers.forEach((value, key) => {
        if (!ignoredHeaders.has(key.toLowerCase())) {
          config.headers[key] = value;
        }
      });
      config.headers.cookie = request.headers.get('cookie') || '';
      // set Referer so backend trusts request and doesn't mistake as API request
      // only needed on server, since client automatically adds referer
      // needs to not be empty or a different domain
      config.headers.Referer =
        request.headers.get('referer') || import.meta.env.VITE_ORIGIN! || '';
      if (
        config.headers.Referer.startsWith(import.meta.env.VITE_ORIGIN!) ===
        false
      ) {
        // replace 3rd party actual referer with www so API accepts request
        config.headers.Referer = `${import.meta.env.VITE_ORIGIN}?ssrReferer=${config.headers.Referer}`;
      }

      // forward real user agent instead of axios default
      const userAgent = request.headers.get('user-agent');
      if (userAgent) {
        config.headers['user-agent'] = userAgent;
      }

      // add forwarded ip address so cloudflare knows who to murder
      ['cf-connecting-ip', 'x-real-ip', 'x-forwarded-for'].forEach((key) => {
        const value = request.headers.get(key);
        if (value) {
          config.headers[key] = value;
        }
      });

      // replace request hostname with localhost for server-to-server API requests
      if (config.url && isApiUrl(config.baseURL)) {
        // replace URL to localhost to speed up server-side API requests
        config.url = config.url.replace(
          new RegExp(`^${import.meta.env.VITE_API_URL}`),
          import.meta.env.VITE_API_LOCALHOST!
        );
      }
    }

    if (config.url?.startsWith(import.meta.env.VITE_API_URL + '/')) {
      const url = new URL(config.url!);
      url.searchParams.append('nextStatic', '1');
      config.url = url.toString();
    }
  }
  logger.debug(`[$axios][request →]`, {
    method: config.method?.toUpperCase(),
    url: config.url,
    params: config.params,
    headers: config.headers,
    // ...config,
  });
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  logger.warn(`[$axios][request → error]`, {
    method: error.config?.method?.toUpperCase(),
    url: error.config?.url,
    error: error.message,
  });
  return Promise.reject(error);
};

const onResponse = (
  response: AxiosResponse,
  request?: Request
): AxiosResponse => {
  logger.debug(
    `[$axios][← response][${response.request?.method?.toUpperCase()}][${
      response.status
    }]`,
    {
      url: response.config.url,
      // headers: response.headers,
      // body: response.data,
    }
  );

  // forward on set-cookie headers from api response
  if (isServer() && request) {
    if (isApiUrl(response.config.baseURL) && response.headers['set-cookie']) {
      request.headers.set(
        'set-cookie',
        response.headers['set-cookie'].join('; ')
      );
    }
  }
  return response;
};

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  logger.debug(
    `[$axios][← response error][${error.config?.method?.toUpperCase()}][${
      error.status
    }]`,
    {
      url: error.config?.url,
      Referer: `${error.config?.headers.Referer}`,
      message: error.message,
    },
    error.response?.data
  );

  if (!isServer() && error.response?.headers?.['x-nd-invalid-session-hash']) {
    const refreshParam = 'session-hash-refresh';
    const url = new URL(location.href);
    if (url.searchParams.has(refreshParam)) {
      // console.warn('not refreshing session hash again');
    } else {
      url.searchParams.append(refreshParam, '1');
      location.href = url.href;
    }
  }
  return Promise.reject(error);
};

function setupInterceptorsTo(
  axiosInstance: AxiosInstance,
  request?: Request
): AxiosInstance {
  // @ts-expect-error custom field
  if (axiosInstance.intercepted) {
    return axiosInstance;
  }
  // @ts-expect-error custom field
  axiosInstance.intercepted = true;
  axiosInstance.interceptors.request.use(
    (config) => onRequest(config, request),
    onRequestError
  );
  axiosInstance.interceptors.response.use(
    (response) => onResponse(response, request),
    onResponseError
  );
  return axiosInstance;
}

export default function getAxiosInstance(request?: Request): AxiosInstance {
  const axiosInstance = axios.create({
    baseURL: isServer()
      ? import.meta.env.VITE_API_LOCALHOST
      : import.meta.env.VITE_API_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    timeout: 60e3,
  });

  return setupInterceptorsTo(axiosInstance, request);
}

export interface SerializedAxiosError {
  name: string;
  message: string;
  code?: string;
  status?: number;
  config?: InternalAxiosRequestConfig;
  data?: Record<string, unknown> | undefined;
  headers?: AxiosResponse['headers'];
}

export function serializeAxiosError(
  error: AxiosError | Error
): SerializedAxiosError {
  if (isAxiosError(error)) {
    const serverErrorMessage = error?.response?.data?.message;
    return {
      name: error.name,
      message: serverErrorMessage || error.message,
      code: error.response?.data?.code || error.code,
      config: error.config,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers,
    };
  }
  return error;
}

/**
 * Rethrow an axios error as a route error response, so the ErrorBoundary
 * shows the API's error message instead of axios' generic
 * "Request failed with status code ..." message.
 *
 * eg: `await api.get('/foo').catch(throwAxiosErrorResponse)`
 */
export function throwAxiosErrorResponse(error: unknown): never {
  if (isAxiosError(error) && error.response) {
    const { message, status } = serializeAxiosError(error);
    throw data(message, { status });
  }
  throw error;
}

export function deserializeAxiosError(
  serializedError: SerializedAxiosError | undefined | null
): AxiosError | Error | null {
  if (!serializedError) {
    return null;
  }
  if ('name' in serializedError === false) {
    return JSON.parse(serializedError) as Error;
  }
  const error = new AxiosError(
    (serializedError.data?.message || serializedError.message) as string,
    serializedError.code,
    serializedError.config,
    undefined,
    {
      status: serializedError.status || 0,
      data: serializedError.data,
      headers: serializedError.headers || {},
      config: {} as InternalAxiosRequestConfig,
      statusText: serializedError.code || '',
    }
  );

  return error;
}

function isApiUrl(baseURL: string | undefined): boolean {
  if (!baseURL) {
    return false;
  }

  if (baseURL.startsWith(import.meta.env.VITE_API_URL!)) {
    return true;
  }
  if (baseURL.startsWith(import.meta.env.VITE_API_LOCALHOST!)) {
    return true;
  }
  return false;
}
